import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  CustomButton,
  CustomSectionTitle,
  CustomTextInput,
  CustomSingleSelect,
  CustomTable,
  MobileTable,
  CustomCheckbox,
  CustomTruncateText,
  type PaginationInfo,
  type TableProps,
} from "@ippon/shared-components";
import { CustomTwoColInputGroup } from "@ippon/shared-components";
import { Add } from "@mui/icons-material";
import { SendMailIcon, RequireIcon } from "@ippon/shared-assets";
import { useForm } from "react-hook-form";
import { CustomModal } from "@ippon/shared-components";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/store";
import {
  searchEmployees,
  deleteEmployee,
  createEmployee,
  sendEmployeeResetEmail,
} from "@/store/employeeSlice";
import { fetchDetailedClient } from "@/store/clientSlice";
import { getEmployeeID, getClientID } from "@/utils/authUtils";
import { useCan } from "@/hooks/useCan";
import { useNavigate } from "react-router-dom";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { useOwnAccountRole } from "@/hooks/useOwnAccountRole";
const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));
const formatDateTime = (input: string | Date | null | undefined): string => {
  if (!input) return "";
  const d = typeof input === "string" ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mm = pad2(d.getMinutes());
  return `${y}/${m}/${day} ${hh}:${mm}`;
};

type SearchForm = { name: string; perPage: number };
type CreateForm = {
  last_name: string;
  first_name: string;
  last_name_kana: string;
  first_name_kana: string;
  mail_address: string;
  role?: "admin" | "general";
  // アカウントロール（auth_roles tier=account）
  account_role?: "member" | "admin" | "owner";
  service_ids: string[];
};

// アカウントロールの選択肢（auth_roles tier=account の label と一致させる）
const ACCOUNT_ROLE_OPTIONS = [
  { value: "owner", label: "オーナー" },
  { value: "admin", label: "管理者" },
  { value: "member", label: "一般" },
];

const ACCOUNT_ROLE_LABELS: Record<string, string> = Object.fromEntries(
  ACCOUNT_ROLE_OPTIONS.map(({ value, label }) => [value, label])
);

// アカウントロールのランク（auth_roles tier=account の rank と一致させる。大きいほど上位）
const ACCOUNT_ROLE_RANK: Record<string, number> = { member: 10, admin: 20, owner: 30 };

const AccountSetting: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { loading: isSearchLoading } = useSelector(
    (state: any) => state.employees.searched
  );
  const { showToast } = useGlobalToast();

  const getApiErrorMessage = (res: any, defaultMessage: string): string => {
    if (typeof res?.payload === "string") {
      return res.payload;
    }
    return res?.payload?.message || defaultMessage;
  };

  const { register, setValue, control, getValues } = useForm<SearchForm>({
    defaultValues: { name: "", perPage: 50 },
  });

  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    formState: { errors: createErrors },
    reset: resetCreate,
    setValue: setValueCreate,
    watch: watchCreate,
  } = useForm<CreateForm>({
    defaultValues: {
      last_name: "",
      first_name: "",
      last_name_kana: "",
      first_name_kana: "",
      mail_address: "",
      role: "general",
      account_role: "member",
      service_ids: [],
    },
  });

  const [rows, setRows] = useState<any[]>([]);
  const [rawEmployees, setRawEmployees] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  const [perPage, setPerPage] = useState<number>(50);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [deletePayload, setDeletePayload] = useState<
    { employee_id: string; mail_address: string }[]
  >([]);
  const [openSelfDeleteModal, setOpenSelfDeleteModal] = useState(false);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  // シート購入確認ダイアログ（残席0のサービスを含む作成時に表示）
  const [openPurchaseModal, setOpenPurchaseModal] = useState(false);
  const [pendingCreateData, setPendingCreateData] = useState<any>(null);
  const can = useCan();
  const canEditEmployees = can("employee:edit");
  const canDeleteEmployees = can("employee:delete");
  const clientId = getClientID();
  const deleteButtonSx =
    selectedIds.length > 0
      ? {
          backgroundColor: "#c82333 !important",
          "&:hover": { backgroundColor: "#c82333 !important" },
          "&:active": { backgroundColor: "#bd2130 !important" },
        }
      : {
          backgroundColor: "#808080 !important",
          "&:hover": { backgroundColor: "#808080 !important" },
          "&:active": { backgroundColor: "#808080 !important" },
        };

  // ログイン中の自分自身のアカウントロール。自分より高いロールは新規作成時に選べないようにする
  const { accountRole: ownAccountRole, loading: loadingOwnAccountRole } =
    useOwnAccountRole();
  const ownAccountRoleRank = ownAccountRole
    ? ACCOUNT_ROLE_RANK[ownAccountRole] ?? 0
    : 0;
  const allowedAccountRoleOptions = loadingOwnAccountRole
    ? ACCOUNT_ROLE_OPTIONS
    : ACCOUNT_ROLE_OPTIONS.filter(
        (opt) => (ACCOUNT_ROLE_RANK[opt.value] ?? 0) <= ownAccountRoleRank
      );

  const { data: clientData } = useSelector(
    (state: any) => state.clients.detailed
  );

  // フォームの値を監視
  const createFormValues = watchCreate();
  const isCreateFormValid =
    createFormValues.last_name?.trim() !== "" &&
    createFormValues.first_name?.trim() !== "" &&
    createFormValues.last_name_kana?.trim() !== "" &&
    createFormValues.first_name_kana?.trim() !== "" &&
    createFormValues.mail_address?.trim() !== "" &&
    selectedServiceIds &&
    selectedServiceIds.length > 0;

  // 選択サービスのうち、残席0（契約あり）のものがあれば席の新規購入が発生する
  const willPurchaseSeat = (): boolean => {
    const services = clientData?.client_services ?? [];
    return services.some(
      (cs: any) =>
        selectedServiceIds.includes(cs.service.id) && cs.free_seats === 0
    );
  };

  // アカウント作成の実処理（確認不要時の直接作成と、購入確認ダイアログの「作成」で共用）
  const doCreateAccount = async (data: any) => {
    try {
      setIsCreating(true);
      const payload = { ...data, service_ids: selectedServiceIds };
      const res = (await dispatch(createEmployee(payload) as any)) as any;
      if (res?.meta?.requestStatus === "fulfilled") {
        showToast({ type: "success", message: "作成しました" });
        setOpenPurchaseModal(false);
        setPendingCreateData(null);
        setOpenCreateModal(false);
        resetCreate();
        setSelectedServiceIds([]);
        fetchEmployees(1, perPage);
        // 残席数を最新化（購入・占有による変化を反映）
        if (clientId) dispatch(fetchDetailedClient(clientId) as any);
      } else {
        const errorMessage = getApiErrorMessage(res, "作成に失敗しました");
        showToast({ type: "error", message: errorMessage });
      }
    } catch (e) {
      showToast({ type: "error", message: "作成に失敗しました" });
    } finally {
      setIsCreating(false);
    }
  };

  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    page: 1,
    limit: 50,
  });

  const perPageOptions = useMemo(
    () => [
      { value: 10, label: "10件" },
      { value: 25, label: "25件" },
      { value: 50, label: "50件" },
      { value: 100, label: "100件" },
    ],
    []
  );

  // Handle row click for navigation
  const handleRowClick = (row: any) => {
    navigate(`/setting/account/${row.id}`);
  };

  const columns: TableProps["columns"] = useMemo(
    () => [
      {
        field: "fullName",
        headerName: "名前",
        flex: 2,
        minWidth: 110,
        renderCell: ({ row }: any) => (
          <Box
            component="a"
            href={`/setting/account/${row.id}`}
            sx={{
              color: "#3e3e3e",
              cursor: "pointer",
              textDecoration: "underline",
              fontSize: 14,
              width: "100%",
              transition: "color 0.2s ease-in-out",
              "&:hover": {
                color: "rgb(14, 157, 188)",
              },
            }}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              handleRowClick(row);
            }}
          >
            <CustomTruncateText
              text={`${row.lastName || ""} ${row.firstName || ""}`}
            />
          </Box>
        ),
      },
      {
        field: "furiganaName",
        headerName: "フリガナ",
        flex: 2,
        minWidth: 110,
        valueGetter: ({ row }: any) =>
          `${row.lastNameOfFurigana || ""} ${row.firstNameOfFurigana || ""}`,
      },
      {
        field: "services",
        headerName: "サービス",
        minWidth: 165,
        renderCell: ({ value }: any) => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              gap: 0.5,
            }}
          >
            {value && value.length > 0 ? (
              value.map((service: string, index: number) => (
                <Typography
                  key={index}
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: 1.7,
                    whiteSpace: "nowrap",
                    color: "rgb(62, 62, 62)",
                    backgroundColor: "rgb(240, 240, 240)",
                    textAlign: "left",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    display: "block",
                  }}
                >
                  {service}
                </Typography>
              ))
            ) : (
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: 1.7,
                  color: "#000000",
                }}
              >
                -
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: "email",
        headerName: "メールアドレス",
        flex: 3,
        minWidth: 150,
        renderCell: ({ value }: any) => (
          <Box sx={{ fontSize: 14 }}>
            <CustomTruncateText text={value || ""} />
          </Box>
        ),
      },
      {
        field: "adminStatus",
        headerName: "アカウント権限",
        width: 130,
        renderCell: ({ value }: any) => (
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: 1.7,
              color: value !== "-" ? "#0E9DBC" : "#000000",
              backgroundColor: value !== "-" ? "#DBF8FF" : "transparent",
              textAlign: "left",
              padding: value !== "-" ? "2px 8px" : 0,
              borderRadius: "6px",
              display: "inline-block",
              minWidth: value !== "-" ? "fit-content" : "auto",
            }}
          >
            {value}
          </Typography>
        ),
      },
      { field: "registration_timestamp", headerName: "登録日時", flex: 1 },
      { field: "update_timestamp", headerName: "更新日時", flex: 1 },
      {
        field: "actions",
        headerName: "",
        width: 210,
        renderCell: (params: any) =>
          params.row.id === getEmployeeID() || canEditEmployees ? (
            <CustomButton
              label="パスワード変更"
              buttonCategory="passwordReset"
              startIcon={<SendMailIcon />}
              onClick={async () => {
                try {
                  const res = (await dispatch(
                    sendEmployeeResetEmail(params.row.email) as any
                  )) as any;
                  const status = res?.meta?.requestStatus;
                  if (status === "fulfilled") {
                    showToast({
                      type: "success",
                      message: "メールを正常に送信いたしました",
                    });
                  } else {
                    showToast({
                      type: "error",
                      message: "メール送信に失敗しました",
                    });
                  }
                } catch (e) {
                  showToast({
                    type: "error",
                    message: "メール送信に失敗しました",
                  });
                }
              }}
            />
          ) : (
            <Typography sx={{ fontSize: "14px" }}>-</Typography>
          ),
      },
    ],
    [canEditEmployees]
  );

  // Backend fetch
  const fetchEmployees = async (page = 1, limit = perPage, name = "") => {
    try {
      setIsLoading(true);
      const query = { page, limit, name } as any;
      const result = (await dispatch(searchEmployees(query) as any)) as any;
      const data = result?.payload;
      if (data) {
        const items = data.items || [];
        setRawEmployees(items);
        setPagination({
          total: data.total ?? 0,
          page: data.page ?? page,
          limit: data.limit ?? limit,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // const onSubmit = (data: SearchForm) => {
  //   setSelectedIds([]);
  //   fetchEmployees(1, perPage, data.name);
  // };

  const handleSearch = () => {
    const searchData = getValues();
    setSelectedIds([]);
    fetchEmployees(1, perPage, searchData.name || "");
  };

  const clearSearch = () => {
    setValue("name", "");
    setSelectedIds([]);
    fetchEmployees(1, perPage, "");
  };

  const handlePageChange = (page: number) => {
    fetchEmployees(page, pagination.limit);
  };

  const handlePerPageChange = (value: number) => {
    setPerPage(value);
    fetchEmployees(1, value);
  };

  const handleOpenDeleteModal = () => {
    if (selectedIds.length === 0) return;
    if (!canDeleteEmployees) {
      showToast({ type: "error", message: "削除権限がありません" });
      return;
    }
    const currentEmployeeID = getEmployeeID();
    const isSelfSelected = selectedIds.includes(currentEmployeeID);
    if (isSelfSelected) {
      setOpenSelfDeleteModal(true);
      return;
    }
    setOpenDeleteModal(true);
  };

  const handleDelete = async () => {
    setOpenDeleteModal(false);
    if (deletePayload.length === 0) return;
    try {
      // setIsDeleting(true);
      const res = (await dispatch(deleteEmployee(deletePayload) as any)) as any;

      if (res?.meta?.requestStatus === "fulfilled") {
        const payload = res?.payload;
        if (payload?.deleted_employees?.length > 0) {
          showToast({ type: "success", message: "削除に成功しました" });
          setSelectedIds([]);
          setDeletePayload([]);
          fetchEmployees(pagination.page, pagination.limit);
          // 削除で占有が解放され空き枠が増えるため、残席数（free_seats）を再取得して最新化
          if (clientId) dispatch(fetchDetailedClient(clientId) as any);
        } else {
          showToast({ type: "error", message: "削除に失敗しました" });
        }
      } else {
        const errorMessage = getApiErrorMessage(res, "削除に失敗しました");
        showToast({ type: "error", message: errorMessage });
      }
    } catch (e) {
      showToast({ type: "error", message: "削除に失敗しました" });
    } finally {
      // setIsDeleting(false);
    }
  };

  // Selection compute delete payload
  const onRowSelection = (ids: any[]) => {
    setSelectedIds(ids);
    const selected = rows
      .filter((r) => ids.includes(r.id))
      .map((r) => ({ employee_id: r.id, mail_address: r.email }));
    setDeletePayload(selected);
  };

  // Initial load
  useEffect(() => {
    fetchEmployees(1, perPage, "");
    if (clientId) {
      dispatch(fetchDetailedClient(clientId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // rawEmployeesが変更されたときにrowsを再計算
  useEffect(() => {
    if (rawEmployees.length > 0) {
      setRows(
        rawEmployees.map((employee: any, index: number) => {
          // assigned_servicesからサービス名を取得
          let services: string[] = [];
          if (
            employee.assigned_services &&
            Array.isArray(employee.assigned_services)
          ) {
            services = employee.assigned_services
              .map((assignedService: any) => assignedService?.service?.name)
              .filter(Boolean);
          }

          return {
            id: employee.id || index,
            lastName: employee.last_name || "",
            firstName: employee.first_name || "",
            lastNameOfFurigana: employee.last_name_kana || "",
            firstNameOfFurigana: employee.first_name_kana || "",
            email: employee.mail_address || "",
            services: services,
            adminStatus:
              ACCOUNT_ROLE_LABELS[employee.account_role] ||
              (employee.is_admin ? "管理者" : "-"),
            registration_timestamp: formatDateTime(employee.created_at),
            update_timestamp: formatDateTime(employee.updated_at),
          };
        })
      );
    }
  }, [rawEmployees]);

  // remove mock effect

  return (
    <Box pb={3} pt={{ xs: 2, sm: 3 }}>
      <CustomSectionTitle title="アカウント設定" size="large" />

      {/* Search */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        marginTop={2}
        marginBottom={1}
      >
        {/* <form onSubmit={handleSubmit(onSubmit)}> */}
        <Stack direction="row" spacing={1.25} alignItems="center" flex={1}>
          <Box flex={1} maxWidth="400px">
            <Box
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            >
              <CustomTextInput
                label=""
                name="name"
                type="text"
                placeholder="キーワードで絞り込む"
                register={register as any}
                errors={{}}
                inputWidth="100%"
                showSearchIconStart
                search={true}
                hasSpacing={false}
              />
            </Box>
          </Box>
          <CustomButton
            type="button"
            label="検索"
            buttonCategory="search"
            onClick={handleSearch}
            spIcon={true}
          />
          <CustomButton
            type="button"
            label="クリア"
            onClick={clearSearch}
            buttonCategory="clear"
            spIcon={true}
          />
        </Stack>
        {/* </form> */}
      </Stack>

      {/* Result and actions */}
      {/* Desktop: Result and actions in one row */}
      <Box sx={{ display: { xs: "none", md: "block" }, mb: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ gap: 2 }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              mr: "12px !important",
            }}
          >
            検索結果{" "}
            <span style={{ color: "#0B9DBD", margin: "0 7px" }}>
              {pagination.total}
            </span>{" "}
            件
          </Typography>
          <Stack direction="row" gap={1} alignItems="center">
            <Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: "14px", color: "#3e3e3e" }}
              >
                表示件数
              </Typography>
            </Box>
            <Box sx={{ mt: 1 }}>
              <CustomSingleSelect
                label=""
                name="perPage"
                placeholder=""
                control={control}
                errors={{}}
                inputWidth="80px"
                inputWidthSp="100%"
                options={perPageOptions}
                onChangeValue={(v) => handlePerPageChange(Number(v))}
              />
            </Box>
            {(canEditEmployees || canDeleteEmployees) && (
              <>
                {canEditEmployees && (
                  <CustomButton
                    label="新規追加"
                    startIcon={<Add sx={{ fontSize: "18px !important" }} />}
                    onClick={() => setOpenCreateModal(true)}
                  />
                )}
                {canDeleteEmployees && (
                  <CustomButton
                    label="削除"
                    onClick={handleOpenDeleteModal}
                    buttonCategory="delete"
                    sx={deleteButtonSx}
                  />
                )}
              </>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Mobile: Result centered above table, actions above result */}
      <Box sx={{ display: { xs: "block", md: "none" }, mb: 2 }}>
        <Stack direction="column" spacing={2}>
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            sx={{
              flexWrap: "wrap",
              justifyContent: "flex-start",
            }}
          >
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography
                sx={{ fontWeight: 700, fontSize: "14px", color: "#3e3e3e" }}
              >
                表示件数
              </Typography>
            </Box>
            <Box sx={{ mt: 1 }}>
              <CustomSingleSelect
                label=""
                name="perPage"
                placeholder=""
                control={control}
                errors={{}}
                inputWidth="80px"
                inputWidthSp="100%"
                options={perPageOptions}
                onChangeValue={(v) => handlePerPageChange(Number(v))}
              />
            </Box>
            {(canEditEmployees || canDeleteEmployees) && (
              <>
                {canEditEmployees && (
                  <CustomButton
                    label="新規追加"
                    startIcon={<Add sx={{ fontSize: "18px !important" }} />}
                    onClick={() => setOpenCreateModal(true)}
                  />
                )}
                {canDeleteEmployees && (
                  <CustomButton
                    label="削除"
                    onClick={handleOpenDeleteModal}
                    buttonCategory="delete"
                    sx={deleteButtonSx}
                  />
                )}
              </>
            )}
          </Stack>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
              }}
            >
              検索結果{" "}
              <span style={{ color: "#0B9DBD", margin: "0 7px" }}>
                {pagination.total}
              </span>{" "}
              件
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Table */}
      {isMobile ? (
        <MobileTable
          isLoading={isLoading || isSearchLoading}
          rows={rows}
          columns={columns}
          onRowSelection={onRowSelection}
          selectedIds={selectedIds}
          pagination={pagination}
          onPageChange={handlePageChange}
          checkBoxOpen={true}
          handlePropertyClick={handleRowClick}
          disableNameClick={false}
        />
      ) : (
        <CustomTable
          isLoading={isLoading || isSearchLoading}
          rows={rows}
          columns={columns}
          onRowSelection={onRowSelection}
          selectedIds={selectedIds}
          pagination={pagination}
          onPageChange={handlePageChange}
          checkBoxOpen
          enableCheckboxSelection
          initialSort={{ field: "registration_timestamp", direction: "asc" }}
        />
      )}

      {/* Create Modal（標準: UnitCreateModal と同じ registerModal パターン） */}
      <CustomModal
        title="新規アカウント追加登録"
        modalType="registerModal"
        openModal={openCreateModal}
        handleCloseModal={() => {
          setOpenCreateModal(false);
          setSelectedServiceIds([]);
        }}
        addTitleBorder="true"
        dynamicHeight
      >
        <Box className="registerModalBody">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0,
              maxWidth: "600px",
              mx: "auto",
            }}
          >
          <CustomTwoColInputGroup
            label="氏名"
            firstName="last_name"
            lastName="first_name"
            placeholderOne="山田"
            placeholderTwo="太郎"
            register={registerCreate as any}
            errors={createErrors as any}
            isRequired
            inputWidth="100%"
            inputWidthSp="100%"
          />
          <CustomTwoColInputGroup
            label="フリガナ"
            firstName="last_name_kana"
            lastName="first_name_kana"
            placeholderOne="ヤマダ"
            placeholderTwo="タロウ"
            register={registerCreate as any}
            errors={createErrors as any}
            isRequired
            inputWidth="100%"
            inputWidthSp="100%"
          />
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                mb: 1,
                color: "#3e3e3e",
              }}
            >
              アカウント権限
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              {allowedAccountRoleOptions.map((option) => (
                <Box
                  key={option.value}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <CustomCheckbox
                    checked={watchCreate("account_role") === option.value}
                    onChange={() =>
                      setValueCreate(
                        "account_role",
                        option.value as CreateForm["account_role"]
                      )
                    }
                    size="small"
                  />
                  <Box
                    component="label"
                    sx={{
                      fontSize: "14px",
                      color: "#333",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={() =>
                      setValueCreate(
                        "account_role",
                        option.value as CreateForm["account_role"]
                      )
                    }
                  >
                    {option.label}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                mb: 1,
                color: "#3e3e3e",
              }}
            >
              サービス権限
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CustomCheckbox
                checked={watchCreate("role") === "admin"}
                onChange={() => {
                  const currentRole = watchCreate("role");
                  const newRole = currentRole === "admin" ? "general" : "admin";
                  setValueCreate("role", newRole);
                }}
                size="small"
              />
              <Box
                component="label"
                sx={{
                  fontSize: "14px",
                  color: "#333",
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => {
                  const currentRole = watchCreate("role");
                  const newRole = currentRole === "admin" ? "general" : "admin";
                  setValueCreate("role", newRole);
                }}
              >
                管理者
              </Box>
            </Box>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                mb: 1,
                color: "#3e3e3e",
                display: "flex",
                alignItems: "center",
              }}
            >
              利用サービス
              <span style={{ display: "flex", marginLeft: "4px" }}>
                <RequireIcon />
              </span>
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              {clientData?.client_services?.map((cs: any) => (
                <Box
                  key={cs.service.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CustomCheckbox
                    checked={selectedServiceIds.includes(cs.service.id)}
                    onChange={() => {
                      setSelectedServiceIds((prev) =>
                        prev.includes(cs.service.id)
                          ? prev.filter((id) => id !== cs.service.id)
                          : [...prev, cs.service.id]
                      );
                    }}
                    size="small"
                  />
                  <Box
                    component="label"
                    sx={{
                      fontSize: "14px",
                      color: "#333",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={() => {
                      setSelectedServiceIds((prev) =>
                        prev.includes(cs.service.id)
                          ? prev.filter((id) => id !== cs.service.id)
                          : [...prev, cs.service.id]
                      );
                    }}
                  >
                    {cs.service.name}
                  </Box>
                  {typeof cs.free_seats === "number" && (
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: cs.free_seats === 0 ? "#FF9800" : "#666",
                        whiteSpace: "nowrap",
                      }}
                    >
                      残りシート: {cs.free_seats}
                      {cs.free_seats === 0 ? "（新規購入）" : ""}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          <CustomTextInput
            label="メールアドレス"
            name="mail_address"
            type="email"
            placeholder="taro@example.com"
            register={registerCreate as any}
            errors={createErrors}
            isRequired
            inputWidth="100%"
            inputWidthSp="100%"
          />

          </Box>
        </Box>
        <Box className="registerModalFooter">
          <CustomButton
            label="戻る"
            buttonCategory="cancelModal"
            onClick={() => {
              setOpenCreateModal(false);
              setSelectedServiceIds([]);
            }}
            disabled={isCreating}
            sx={{ minWidth: "140px !important", width: "140px" }}
          />
          <CustomButton
            label="作成"
            buttonCategory="submit"
            isLoading={isCreating}
            disabled={
              !isCreateFormValid ||
              isCreating ||
              Object.keys(createErrors).length > 0
            }
            onClick={handleSubmitCreate(async (data) => {
              // 残席0のサービスを含む場合は購入確認ダイアログを挟む
              if (willPurchaseSeat()) {
                setPendingCreateData(data);
                setOpenPurchaseModal(true);
                return;
              }
              await doCreateAccount(data);
            })}
            sx={{ minWidth: "140px !important", width: "140px" }}
          />
        </Box>
      </CustomModal>

      {/* シート購入確認ダイアログ（残席0のサービスを含む作成時）
          幅・ボタン構成は科目新規登録ダイアログ(BillingItemsTable)と同じ deleteModal に統一 */}
      <CustomModal
        title="シート購入の確認"
        openModal={openPurchaseModal}
        handleCloseModal={() => setOpenPurchaseModal(false)}
        modalType="deleteModal"
        bodyText={
          "新規でシートが購入されますがよろしいでしょうか？\n（契約期間は契約内容によります）"
        }
      >
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 2 }}>
          <CustomButton
            label="戻る"
            buttonCategory="cancelModal"
            onClick={() => setOpenPurchaseModal(false)}
          />
          <CustomButton
            label="作成"
            buttonCategory="submit"
            isLoading={isCreating}
            disabled={isCreating}
            onClick={async () => {
              if (pendingCreateData) await doCreateAccount(pendingCreateData);
            }}
            sx={{ width: "50% !important" }}
          />
        </Box>
      </CustomModal>

      {/* Delete Modal */}
      <CustomModal
        title="削除の確認"
        openModal={openDeleteModal}
        handleCloseModal={() => setOpenDeleteModal(false)}
        hideCloseButton
        modalType="deleteModal"
      >
        <Typography sx={{ fontSize: "14px" }}>
          選択した{deletePayload.length}件のアカウントを削除します。
          <br />
          この操作は取り消せません。削除してよろしいですか？
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <CustomButton
            label="戻る"
            buttonCategory="cancelModal"
            onClick={() => setOpenDeleteModal(false)}
          />
          <CustomButton
            label="削除"
            onClick={handleDelete}
            buttonCategory="deleteModal"
          />
        </Box>
      </CustomModal>

      <CustomModal
        title="削除エラー"
        openModal={openSelfDeleteModal}
        handleCloseModal={() => setOpenSelfDeleteModal(false)}
        bodyText="自身のアカウントを削除することはできません"
      >
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CustomButton
            label="戻る"
            buttonCategory="cancel"
            onClick={() => setOpenSelfDeleteModal(false)}
          />
        </Box>
      </CustomModal>
    </Box>
  );
};

export default AccountSetting;
